import type * as PIXI from 'pixi.js'

export interface Color {
  string: string
  int: number
}

export interface Coordinates {
  latitude: number
  longitude: number
}

export interface SliceIdentifier {
  locationId: string
  categoryId: string
}

export interface Slice {
  id: string
  percentage: number
  color: Color
  nodeCount: number
}

export interface Pie {
  id: string
  coordinates: Coordinates
  totalNodeCount: number
  slices: Slice[]
  radius: number
}

export interface Node {
  id: string
  blobId: string
  colors: Color[]
}

export interface Connection {
  slice: Slice
  node: Node
  thickness: number
}

export interface HighlightedSlice {
  pie: Pie
  slice: Slice
}

export interface Settings {
  initialCoordinates: Coordinates
  initialScale: number
  depotId?: string
}

export interface Visualization {
  pies: Pie[]
  nodes: Node[]
  connections: Connection[]
  hoveredSlice: HighlightedSlice | null
  currentlySelectedSlices: HighlightedSlice[]
  hoveredNode: Node | null
  selectedNodes: Node[]
  settings: Settings
}

export interface D3Pie {
  id: string
  coordinates: Coordinates
  radius: number
  slices: D3Slice[]
}

export interface D3Slice {
  index: number
  startAngle: number
  endAngle: number
  padAngle: number
  value: number
  data: Slice
}

export interface NodesData {
  nodes: Node[]
  pie: Pie
  slice: Slice
}

export interface Entity extends Map<string, PIXI.Graphics> {}

export enum Event {
  REDRAW = 'redraw',
  HIGHLIGHT_SLICES = 'highlightSlices',
  SLICE_CLICKED = 'sliceClicked',
  SLICE_HOVERED = 'sliceHovered',
  SLICE_UNHOVERED = 'sliceUnhovered',
  ANIMATION_COMPLETE = 'animationComplete',
  ADD_NODES = 'addNodes',
  HIDE_NODES = 'hideNodes',
  SHOW_NODES = 'showNodes',
  NODE_CLICKED = 'nodeClicked',
  PIE_POSITIONS_UPDATED = 'piePositionsUpdated'
}

export type ProjectFunction = (coords: [number, number]) => { x: number, y: number }
export type UnprojectFunction = (coords: [number, number]) => { lat: number, lng: number }
export type EventCallback = (data: any) => void
